<script setup lang="ts">
import {computed} from "vue";
import dayjs, {Dayjs} from "dayjs";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamAbsence} from "@/core/interfaces/TeamPlanning";
import {memberColor, memberInitials, memberName, staffMembers} from "@/team/planning";
import {ABSENCE_MOTIFS, AbsenceMotif, formatAbsenceMonth, formatAbsenceRange, motifOf} from "@/planning/lib/absences";
import {ChevronLeft, ChevronRight} from "@lucide/vue";

/** Floor of a day column, below which a month stops being readable and scrolls. */
const DAY_COLUMN_MIN = 24;

/** Height of one stacking lane, and so of a row holding a single absence. */
const LANE_HEIGHT = 28;

const {anchor, members, absences} = defineProps<{
    /** First day of the shown month. */
    anchor: Dayjs;
    members: LegacyAccess[];
    /** The absences overlapping the shown month, cropped here rather than server-side. */
    absences: TeamAbsence[];
}>();

defineEmits<{
    (event: 'go', anchor: Dayjs): void;
    (event: 'select', absence: TeamAbsence): void;
}>();

interface TimelineDay {
    number: number;
    weekend: boolean;
    today: boolean;
}

interface TimelineBar {
    absence: TeamAbsence;
    motif: AbsenceMotif;
    /** Day numbers of the shown month, both bounds included. */
    startDay: number;
    endDay: number;
    /** Whether the absence runs past that edge of the month. */
    cropStart: boolean;
    cropEnd: boolean;
    /** Stacking lane, so overlapping absences never hide one another. */
    lane: number;
}

/** The bars of one member, and how many lanes they need. */
interface TimelineTrack {
    bars: TimelineBar[];
    lanes: number;
}

const staff = computed<LegacyAccess[]>(() => staffMembers(members));

const days = computed<TimelineDay[]>(() => {
    const first = anchor.startOf('month');
    const today = dayjs();

    return Array.from({length: anchor.daysInMonth()}, (_, index) => {
        const date = first.add(index, 'day');

        return {
            number: date.date(),
            weekend: date.day() === 0 || date.day() === 6,
            today: date.isSame(today, 'day')
        };
    });
});

/** One template for every row, so the columns line up across the gutter. */
const template = computed(() => ({
    gridTemplateColumns: `130px repeat(${days.value.length}, minmax(${DAY_COLUMN_MIN}px, 1fr))`
}));

const trackOf = (member: LegacyAccess): TimelineTrack => {
    const first = anchor.startOf('month');
    const last = anchor.endOf('month');

    const placed = absences
        .filter(absence => absence.user_access_point_id === member.id)
        .map(absence => ({
            absence,
            starts: dayjs(absence.starts_at),
            ends: dayjs(absence.ends_at)
        }))
        // The month set lags a month change by one fetch, and a failed fetch
        // keeps it: without this, an absence that does not touch the shown
        // month would be re-projected onto its day columns.
        .filter(({starts, ends}) => !ends.isBefore(first, 'day') && !starts.isAfter(last, 'day'))
        .map(({absence, starts, ends}) => {
            const cropStart = starts.isBefore(first, 'day');
            const cropEnd = ends.isAfter(last, 'day');

            return {
                absence,
                motif: motifOf(absence.reason),
                startDay: cropStart ? 1 : starts.date(),
                endDay: cropEnd ? last.date() : ends.date(),
                cropStart,
                cropEnd,
                lane: 0
            };
        })
        .sort((a, b) => a.startDay - b.startDay || a.endDay - b.endDay);

    // Nothing forbids two absences over the same days — the server validates
    // no overlap — so each one takes the first lane still free on its span.
    const lastDayOfLane: number[] = [];

    placed.forEach(bar => {
        const lane = lastDayOfLane.findIndex(endDay => endDay < bar.startDay);

        bar.lane = lane === -1 ? lastDayOfLane.length : lane;
        lastDayOfLane[bar.lane] = bar.endDay;
    });

    return {bars: placed, lanes: Math.max(lastDayOfLane.length, 1)};
};

/** Column 1 is the gutter, so a day number sits one column further right. */
const barStyle = (bar: TimelineBar) => ({
    gridColumn: `${bar.startDay + 1} / ${bar.endDay + 2}`,
    gridRow: bar.lane + 1
});

/** Resolved once per render: the template and the legend read the same pass. */
const tracks = computed(() => staff.value.map(member => ({member, track: trackOf(member)})));

/** A member's row grows with the lanes their absences need. */
const trackStyle = (track: TimelineTrack) => ({
    ...template.value,
    gridTemplateRows: `repeat(${track.lanes}, ${LANE_HEIGHT}px)`
});

/**
 * A cropped end stays square while the other keeps its pill: the bar then
 * reads as running past the month rather than ending there.
 */
const barRadius = (bar: TimelineBar): string => {
    if (bar.cropStart && bar.cropEnd) {
        return 'rounded-none';
    }

    if (bar.cropStart) {
        return 'rounded-r-full';
    }

    return bar.cropEnd ? 'rounded-l-full' : 'rounded-full';
};

/** What the grid actually draws, which the notice below must agree with. */
const drawnCount = computed<number>(() => tracks.value.reduce((sum, {track}) => sum + track.bars.length, 0));

/** Only the motives actually drawn, in the catalog's own order. */
const legend = computed<AbsenceMotif[]>(() => {
    const drawn = new Set(tracks.value.flatMap(({track}) => track.bars.map(bar => bar.motif.label)));

    return ABSENCE_MOTIFS.filter(motif => drawn.has(motif.label));
});
</script>

<template>
  <div>
    <div class="mb-2.5 flex items-center justify-center gap-3">
      <button
        type="button"
        title="Mois précédent"
        class="flex size-7 items-center justify-center rounded-lg border border-solid border-gray-200 bg-white text-gray-600 hover:border-gray-300"
        @click="$emit('go', anchor.subtract(1, 'month'))"
      >
        <ChevronLeft class="size-3.5" />
      </button>
      <div class="min-w-[130px] text-center text-sm font-semibold capitalize text-gray-800">
        {{ formatAbsenceMonth(anchor) }}
      </div>
      <button
        type="button"
        title="Mois suivant"
        class="flex size-7 items-center justify-center rounded-lg border border-solid border-gray-200 bg-white text-gray-600 hover:border-gray-300"
        @click="$emit('go', anchor.add(1, 'month'))"
      >
        <ChevronRight class="size-3.5" />
      </button>
    </div>

    <!-- The gutter sticks so a long month stays readable once it scrolls. -->
    <div class="overflow-x-auto rounded-lg border border-gray-200">
      <div class="grid" :style="template">
        <div class="sticky left-0 z-10 bg-white" style="grid-column: 1; grid-row: 1" />
        <div
          v-for="day in days"
          :key="`head-${day.number}`"
          class="py-1 text-center text-[9px] font-semibold"
          :class="[
            day.weekend ? 'bg-gray-50 text-gray-300' : 'text-gray-400',
            day.today ? 'text-primary' : ''
          ]"
          :style="{gridColumn: day.number + 1, gridRow: 1}"
        >
          {{ day.number }}
        </div>
      </div>

      <div
        v-for="{member, track} in tracks"
        :key="member.id"
        class="grid items-center border-t border-gray-100"
        :style="trackStyle(track)"
      >
        <div
          class="sticky left-0 z-10 flex items-center gap-2 bg-white pl-2 pr-2.5"
          style="grid-column: 1; grid-row: 1 / -1"
        >
          <div
            class="flex size-6 flex-none items-center justify-center rounded-full text-[9px] font-semibold text-white"
            :style="{backgroundColor: memberColor(member)}"
          >
            {{ memberInitials(member) }}
          </div>
          <span class="truncate text-[10.5px] font-semibold text-gray-700">
            {{ memberName(member) }}
          </span>
        </div>

        <!-- Spanning every lane: the day columns are the row's background. -->
        <div
          v-for="day in days"
          :key="`cell-${member.id}-${day.number}`"
          class="h-full border-l border-gray-50"
          :class="day.weekend ? 'bg-gray-50' : ''"
          :style="{gridColumn: day.number + 1, gridRow: '1 / -1'}"
        />

        <!-- The bar opens the absence, the same detail the list's pencil shows. -->
        <button
          v-for="bar in track.bars"
          :key="bar.absence.id"
          type="button"
          class="mx-px flex h-5 items-center overflow-hidden px-1.5 text-left transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          :class="[bar.motif.classes, barRadius(bar)]"
          :style="barStyle(bar)"
          :title="`${bar.absence.reason} · ${formatAbsenceRange(bar.absence)} — cliquez pour ouvrir`"
          @click="$emit('select', bar.absence)"
        >
          <span class="truncate text-[9px] font-semibold">{{ bar.absence.reason }}</span>
        </button>
      </div>
    </div>

    <div v-if="!drawnCount" class="mt-2.5 text-center text-[10.5px] text-gray-400">
      Aucune absence sur ce mois.
    </div>
    <div v-else class="mt-2.5 flex flex-wrap items-center gap-1.5">
      <span
        v-for="motif in legend"
        :key="motif.label"
        class="rounded-full px-2 py-0.5 text-[9.5px] font-semibold"
        :class="motif.classes"
      >
        {{ motif.label }}
      </span>
    </div>
  </div>
</template>
